import { useId, useState } from "react";

/**
 * The two ways a BG domain document names something, as controls (#913).
 *
 * ## WHY A PICKER AND NOT A TEXT BOX
 *
 * Every `when`, `trigger_items`, `deny_items`, `gate_item` and `target_items`
 * value is an ITEM ID — the tracker's own key, bounded by a pattern, and
 * resolved against the items of THIS document alone
 * (`background_domains._validate_references`). The ticket's own bound is that
 * "references use item pickers, never raw engine-key entry", and the reason is
 * mechanical rather than stylistic: a typed id is a reference that compiles and
 * silently never fires, because a rule naming an item the domain does not hold
 * is not a cross-document link the runtime follows.
 *
 * So a reference is chosen from the prompts on screen. What CANNOT be chosen —
 * a keyword the engine matches against a captured value — stays free text,
 * because its vocabulary is the patient's language and no catalog exists.
 *
 * ## CHECKBOXES, NOT A MULTI-SELECT
 *
 * A `<select multiple>` is operable but hostile: it needs modifier-clicks, it
 * hides its unselected options behind a scroll on every platform, and its
 * selected state is announced only as a count. A `<fieldset>` of checkboxes
 * gives each reference its own name, its own toggle and its own announcement,
 * and lets the prompt text sit beside the id — which is what makes a list of
 * twelve military items readable at all.
 *
 * ## AND NOT A `<ul>` EITHER
 *
 * The options were briefly marked up as a list, which is defensible content
 * semantics and wrong here: these pickers sit INSIDE the `<li>` of a rule,
 * inside the ordered rules list, so every `listitem` query over the rules
 * counted checkboxes as rules. The grouping a screen reader needs is already
 * carried by `<fieldset>`/`<legend>` — the list added a second, misleading one.
 * The quality-example rows stay an `<ol>`, because their order is content.
 */

export interface ReferenceOption {
  /** The item id written into the document. */
  id: string;
  /** The prompt text, for a clinician who does not read ids. */
  description: string;
}

export function ItemReferencePicker({
  legend,
  description,
  options,
  selected,
  readOnly,
  emptyLabel,
  onChange,
}: {
  legend: string;
  description?: string;
  options: readonly ReferenceOption[];
  selected: readonly string[];
  readOnly: boolean;
  /** What to say when the domain has no prompt to reference. */
  emptyLabel: string;
  onChange: (next: string[]) => void;
}) {
  const describedBy = useId();
  const chosen = new Set(selected);
  // SELECTED, BUT NO LONGER A PROMPT OF THIS DOMAIN. Rendered as its own checked
  // option, for the reason `ReferenceSelect` keeps a missing single reference
  // visible: this picker used to draw `options` alone, so a reference to a
  // deleted prompt was INVISIBLE — and `toggle` rebuilt the next value from
  // `options` alone, so the first unrelated click dropped it silently. A
  // clinical reference must be removed because somebody decided to, never
  // because they ticked a different box.
  const missing = selected.filter(
    (id) => !options.some((option) => option.id === id),
  );
  const rows: (ReferenceOption & { missing?: boolean })[] = [
    ...options,
    ...missing.map((id) => ({ id, description: "", missing: true })),
  ];

  // THE STORED ORDER IS PRESERVED, and the earlier comment here had the
  // reasoning exactly backwards. It said rebuilding in display order kept a
  // version diff from looking like a reordering; rebuilding in display order IS
  // the reordering — a stored `[b, a]` came back as `[a, b]` the moment any
  // other box was ticked, and `Set`-based membership collapsed a duplicate the
  // document legitimately held. The ratified contract's rule is that lists
  // preserve source order, so a tick APPENDS and an untick REMOVES IN PLACE.
  const toggle = (id: string, next: boolean) =>
    onChange(
      next
        ? [...selected, id]
        : selected.filter((option) => option !== id),
    );

  return (
    <fieldset className="space-y-1.5">
      <legend className="text-sm font-medium text-foreground">{legend}</legend>
      {description ? (
        <p id={describedBy} className="text-xs text-muted-foreground">
          {description}
        </p>
      ) : null}
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyLabel}</p>
      ) : (
        <div
          {...(description ? { "aria-describedby": describedBy } : {})}
          className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-border p-2"
        >
          {rows.map((option) => (
            <div key={option.id}>
              <label className="flex items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                  checked={chosen.has(option.id)}
                  disabled={readOnly}
                  aria-invalid={option.missing || undefined}
                  onChange={(event) =>
                    toggle(option.id, event.currentTarget.checked)
                  }
                />
                <span className="min-w-0">
                  <code
                    dir="ltr"
                    className="rounded bg-muted px-1 py-0.5 font-mono text-foreground"
                  >
                    {option.id}
                  </code>
                  {option.missing ? (
                    <span className="ml-2 text-destructive">
                      — no longer a prompt of this domain
                    </span>
                  ) : option.description ? (
                    <span dir="auto" className="ml-2 text-muted-foreground">
                      {option.description}
                    </span>
                  ) : null}
                </span>
              </label>
            </div>
          ))}
        </div>
      )}
    </fieldset>
  );
}

/**
 * A free-text list, one entry per line.
 *
 * ONE PER LINE rather than a comma-separated box, because the entries are
 * KEYWORDS MATCHED AGAINST WHAT A PATIENT SAID — Hebrew phrases that
 * legitimately contain commas, and whose bytes the engine compares
 * case-insensitively against a captured value. A separator inside the
 * vocabulary is a separator that eventually splits a real keyword in half.
 *
 * WHAT IS DROPPED, AND WHAT IS NOT. A completely empty line is dropped from the
 * DOCUMENT: storing it would be storing a blank keyword the service refuses.
 * Every other line is kept VERBATIM, whitespace included — so a whitespace-only
 * line survives to be refused by name rather than silently tidied away, which is
 * the same rule `background_domains._not_blank` follows on the other side.
 *
 * ## THE RAW TEXT IS HELD, AND IT HAS TO BE
 *
 * Rendering `values.join("\n")` straight back made a second keyword
 * IMPOSSIBLE TO TYPE. Pressing Enter produced `"first\n"`, the filter dropped
 * the trailing empty line, the document was unchanged, and the control
 * re-rendered as `"first"` — the newline erased under the cursor, every time.
 *
 * So the buffer is held, exactly as `BackgroundDomainPage` holds the raw
 * interview position for the same class of reason, and staleness is DERIVED
 * rather than synchronised: the buffer is shown only while the lines it would
 * write still equal the ones the document holds. A Discard, a save or somebody
 * else's version changes the document, the comparison fails, and what is on
 * screen is the SERVICE's list again — with no effect to reset it.
 */
export function KeywordListField({
  label,
  description,
  values,
  language,
  readOnly,
  onChange,
}: {
  label: string;
  description: string;
  values: readonly string[] | null;
  language: string | null;
  readOnly: boolean;
  onChange: (next: string[]) => void;
}) {
  const inputId = useId();
  const descriptionId = useId();
  const [raw, setRaw] = useState<string | null>(null);
  const stored = (values ?? []).join("\n");
  const text = raw !== null && entries(raw).join("\n") === stored ? raw : stored;
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={inputId}
        className="block text-sm font-medium text-foreground"
      >
        {label}
      </label>
      <textarea
        id={inputId}
        rows={3}
        aria-describedby={descriptionId}
        className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring read-only:bg-muted"
        value={text}
        readOnly={readOnly}
        dir="auto"
        {...(language ? { lang: language } : {})}
        onChange={(event) => {
          setRaw(event.currentTarget.value);
          onChange(entries(event.currentTarget.value));
        }}
      />
      <p id={descriptionId} className="text-xs text-muted-foreground">
        {description}
      </p>
    </div>
  );
}

/** The lines a textarea's text contributes to the document. */
function entries(text: string): string[] {
  return text.split("\n").filter((line) => line !== "");
}
